import Check from '@mui/icons-material/Check';
import { useQueryClient } from '@tanstack/react-query';
import { InvoiceState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  BulkActionsBar as BulkActionsBarComponent,
  BulkActionsBarButton,
  BulkActionsBarCountButton,
} from '~components/BulkActionsBar/BulkActionsBar';
import pushSettlementCreatedToast from '~components/Toasters/pushSettlementCreatedToast';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { secondsSince } from '~hooks/useAnalytics/useSecondsOnPageTracker';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import useSettlements from '~hooks/useSettlements/useSettlements';
import {
  SettlementForm,
  SettlementFormHandler,
  useSettlementForm,
} from '~pages/Approvals/DriverPay/components/SettlementForm';
import {
  payableInvoiceCategories,
  receivableInvoiceCategories,
} from '~pages/Approvals/DriverPay/constants';
import { TabQSParam } from '~pages/Approvals/DriverPay/TabQSParam';
import { useStores } from '~store';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DriverPayPageContext from '../../DriverPayPageContext';
import { makeInvoicesQueryContextKey } from '../../utils';
const APPROVABLE_STATES = [InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING];
const SETTLEABLE_STATES = [InvoiceState.APPROVED, InvoiceState.CUSTOMER_APPROVED];

export default function BulkActionsBar() {
  const { checkedInvoices, checkedInvoiceIds, uncheckAllInvoices, currentCategory } =
    useContext(DriverPayPageContext);

  // Filter invoices by state and approvable flag to determine which actions are available
  // Note: invoice.approvable is the source of truth from the backend, which considers
  // permissions and other business rules beyond just the state
  const approvableInvoices = useMemo(
    () =>
      checkedInvoices.filter(
        (invoice) => invoice.approvable && APPROVABLE_STATES.includes(invoice.state),
      ),
    [checkedInvoices],
  );
  const settleableInvoices = useMemo(
    () =>
      checkedInvoices.filter(
        (invoice) => !invoice.settlement && SETTLEABLE_STATES.includes(invoice.state),
      ),
    [checkedInvoices],
  );
  const hasApprovableInvoices = approvableInvoices.length > 0;
  const hasSettleableInvoices = settleableInvoices.length > 0;
  const settleableInvoiceIds = useMemo(
    () => settleableInvoices.map((invoice) => invoice.id).filter(Boolean) as string[],
    [settleableInvoices],
  );

  const settlementValidationError = useMemo(() => {
    if (settleableInvoices.length === 0) {
      return undefined;
    }

    const isPayable = payableInvoiceCategories.includes(currentCategory);

    if (isPayable) {
      // settlement creation supports only one vendor/driver entity at a time
      const uniqueEntityIds = new Set(
        settleableInvoices
          .map((invoice) => invoice.vendorAccount?.id ?? invoice.driver?.id)
          .filter(Boolean),
      );
      if (uniqueEntityIds.size > 1) {
        return t('approvals.driver_pay.settlements.multiple_vendors_error');
      }
    }

    const isReceivable = receivableInvoiceCategories.includes(currentCategory);

    if (isReceivable) {
      const uniqueCustomerIds = new Set(
        settleableInvoices.map((invoice) => invoice.customerAccount?.id).filter(Boolean),
      );
      if (uniqueCustomerIds.size > 1) {
        return t('approvals.driver_pay.settlements.multiple_customers_error');
      }
    }

    return undefined;
  }, [settleableInvoices, currentCategory]);

  const [, setSearchParams] = useSearchParams();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const checkedInvoicesCount = checkedInvoiceIds.length;
  const isVisible = checkedInvoicesCount > 0;

  const settlementsFormRef = useRef<SettlementFormHandler>(null);
  const { dialogHandler } = useSettlementForm();
  const { createSettlement } = useSettlements();
  const { approveInvoice } = useInvoices();
  const queryClient = useQueryClient();
  const [isCreatingSettlement, setIsCreatingSettlement] = useState(false);
  const [isApprovingAllInvoices, setIsApprovingAllInvoices] = useState(false);

  const settlementBuildStartedAtRef = useRef(0);

  const handleCreateSettlementClick = useCallback(() => {
    settlementBuildStartedAtRef.current = Date.now();
    AnalyticsController.track('settlement_build_started', {
      source: 'driver_pay_selection',
    });
    dialogHandler.open();
  }, [dialogHandler]);

  const handleSettlementsFormSubmit = useCallback(() => {
    settlementsFormRef.current?.onSubmit?.(async (data) => {
      setIsCreatingSettlement(true);
      try {
        const response = await createSettlement(companyId, data);
        const settlementName = getFriendlySettlementId(response, currentCategory);
        AnalyticsController.track('settlement_created', {
          invoice_count: settleableInvoiceIds.length,
          seconds_from_start: secondsSince(settlementBuildStartedAtRef.current),
          source: 'driver_pay_selection',
        });
        pushSettlementCreatedToast({
          onViewSettlement: () => {
            setSearchParams((prev) => {
              prev.set('tab', TabQSParam.SETTLEMENTS);
              prev.set('search', settlementName);
              return prev;
            });
          },
          settlementName,
          toasterStore,
        });
        dialogHandler.close();
        uncheckAllInvoices();
      } catch {
        // Error already handled in the hook.
      } finally {
        setIsCreatingSettlement(false);
      }
    });
  }, [
    companyId,
    createSettlement,
    currentCategory,
    dialogHandler,
    setSearchParams,
    settleableInvoiceIds.length,
    toasterStore,
    uncheckAllInvoices,
  ]);

  const handleApproveAllInvoicesClick = useCallback(async () => {
    try {
      setIsApprovingAllInvoices(true);

      const approvedInvoices = await Promise.all(
        approvableInvoices.map(async (invoice) => {
          try {
            return await approveInvoice(invoice);
          } catch (invoiceError) {
            console.error(`Error approving invoice:`, invoice, invoiceError);
            throw invoiceError;
          }
        }),
      );

      approvedInvoices.forEach((invoice) => {
        const queryKey = makeInvoicesQueryContextKey(
          String(invoice.order?.id),
          currentCategory,
        );

        queryClient.setQueryData([queryKey], (oldInvoices: Invoice[]) => {
          const oldInvoiceIndex = oldInvoices.findIndex((i) => i.id === invoice.id);

          if (oldInvoiceIndex >= 0) {
            const newInvoices = [...oldInvoices];
            newInvoices.splice(oldInvoiceIndex, 1, invoice);
            return newInvoices;
          }

          return oldInvoices;
        });
      });

      return approvedInvoices;
    } catch (error) {
      console.error('Failed to approve some or all invoices:', error);
      throw error;
    } finally {
      setIsApprovingAllInvoices(false);
      uncheckAllInvoices();
    }
  }, [approvableInvoices, approveInvoice, currentCategory, uncheckAllInvoices]);

  useEffect(() => {
    dialogHandler.updateBusyState(isCreatingSettlement);
  }, [isCreatingSettlement]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <BulkActionsBarComponent>
        <BulkActionsBarCountButton onClick={uncheckAllInvoices}>
          {t('common.count_invoices_selected', {
            count: checkedInvoicesCount,
            plural: checkedInvoicesCount > 1 ? 's' : '',
          })}
        </BulkActionsBarCountButton>

        {hasApprovableInvoices && (
          <BulkActionsBarButton
            color="success"
            loadingPosition="start"
            onClick={handleApproveAllInvoicesClick}
            startIcon={<Check />}
            loading={isApprovingAllInvoices}
          >
            {t('approvals.files.bulk_actions_bar.approve_all_jobs_cta')}
          </BulkActionsBarButton>
        )}

        {hasSettleableInvoices && (
          <BulkActionsBarButton
            color="success"
            loadingPosition="start"
            onClick={handleCreateSettlementClick}
            startIcon={<Check />}
          >
            {t('approvals.driver_pay.create_settlement')}
          </BulkActionsBarButton>
        )}
      </BulkActionsBarComponent>

      <SettlementForm
        ref={settlementsFormRef}
        invoiceIds={settleableInvoiceIds}
        isBusy={dialogHandler.isBusy}
        isOpen={dialogHandler.isOpen}
        onClose={dialogHandler.close}
        onSubmit={handleSettlementsFormSubmit}
        validationError={settlementValidationError}
      />
    </>
  );
}
